import useAnalytics from "@hooks/useAnalytics";
import useApiFetch from "@hooks/useApiFetch";
import useClientContext, { setModal } from "@hooks/useClientContext";
import useGigya from "@hooks/useGigya";
import useSession from "@hooks/useSession";
import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import { pxToRem } from "src/styles/styleUtils";
import { css } from "styled-components";
import { ChaseErrorArea, ChaseInput, ChaseLabel, ChaseStackedRow, ChaseSubmitBtn } from "./Form";
import Modal, {
  ChaseContentContainer,
  ChaseHeaderContent,
  ChaseModalWrapper,
  playoffsModalCSS,
} from "./Modal";

export default function JoinLeagueModal() {
  const [{ openModal }, dispatch] = useClientContext();
  const { data: session } = useSession();
  const { showAuthModal } = useGigya();
  const { fetchApiEndpoint } = useApiFetch();
  const queryClient = useQueryClient();
  const { trackEvent } = useAnalytics();

  const { league } = openModal.options;
  const leagueName = league?.name || "";
  const leagueId = league?.id;
  const isLoggedIn = Boolean(session.user.token);
  const defaultError = "Something went wrong. Please try again.";
  const shouldRenderModal = league && isLoggedIn && openModal.name === "JOIN_LEAGUE";

  interface FormInputs {
    password?: string;
  }
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormInputs>();

  function joinLeague(data: FormInputs) {
    return fetchApiEndpoint(`/api/v1/chase/leagues/${leagueId}/join.json`, {
      headers: { "content-type": "application/json" },
      method: "POST",
      ...(data.password
        ? {
            body: JSON.stringify({
              password: data.password,
            }),
          }
        : {}),
    });
  }

  const joinMutation = useMutation(joinLeague, {
    onSuccess: () => {
      dispatch(setModal(null));
      trackEvent("leagues:join");
      setTimeout(() => {
        queryClient.invalidateQueries(["league", leagueId]);
        queryClient.invalidateQueries("my-leagues");
      }, 1000);
    },
    onError: (error: Error) => {
      const message = `Error: ${error?.message?.replace("base ", "") || defaultError}`;
      setError("password", { type: "manual", message });
    },
  });
  const { isError } = joinMutation;

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    joinMutation.mutate(data);
  };

  useEffect(() => {
    if (openModal.name === "JOIN_LEAGUE" && !isLoggedIn) {
      showAuthModal();
      dispatch(setModal(null));
    }
  }, [openModal.name, showAuthModal, isLoggedIn, dispatch]);

  return shouldRenderModal ? (
    <Modal aria-label="Join League Dialog" css={modalCSS}>
      <ChaseModalWrapper>
        <ChaseContentContainer>
          <ChaseHeaderContent>
            <h2>Join League</h2>
            <p>
              Would you like to join the league &ldquo;<span>{leagueName}</span>&rdquo;?
            </p>
            <form onSubmit={handleSubmit(onSubmit)}>
              {league.is_private && (
                <ChaseStackedRow>
                  <ChaseLabel>Password</ChaseLabel>
                  <ChaseInput
                    type="text"
                    {...register("password", { required: "* League Password is required" })}
                  />
                </ChaseStackedRow>
              )}
              {isError && errors.password && (
                <ChaseErrorArea>{errors?.password?.message || defaultError}</ChaseErrorArea>
              )}
              <ChaseSubmitBtn type="submit">Join League</ChaseSubmitBtn>
            </form>
          </ChaseHeaderContent>
        </ChaseContentContainer>
      </ChaseModalWrapper>
    </Modal>
  ) : null;
}

const modalCSS = css`
  ${playoffsModalCSS}

  .closeIcon {
    top: ${pxToRem(3)};
    right: ${pxToRem(2)};
    svg {
      width: ${pxToRem(14)};
    }
  }
`;
